import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";

import { map } from "rxjs/operators";

import { retryWithBackoff } from "@shared/utility/retry-with-backoff";
import { IHttpRequestOptions, IHttpResponse } from "@schema/http";
import { Pagination } from "@schema/pagination";

@Injectable({
  providedIn: "root"
})
export class ApiService {

  constructor(
    protected _http: HttpClient
  ) { }

  /**
   * Send a GET request
   *
   * @param baseUri   The endpoint to send the data to
   * @param options   Any request options
   */
  public get<T>(baseUri: string, options?: IHttpRequestOptions) {
    const uri = this._buildUri(baseUri, options);
    return this._http.get<T>(uri, { observe: "response" }).pipe(
      retryWithBackoff(options),
      map(response => this._mapSuccessfulResponse<T>(response))
    );
  }

  /**
   * Send a POST request
   *
   * @param baseUri   The endpoint to send the data to
   * @param data      The data to send to the endpoint
   * @param options   Any request options
   */
  public post<T>(baseUri: string, data: any, options?: IHttpRequestOptions) {
    const uri = this._buildUri(baseUri, options);
    return this._http.post<T>(uri, data, { observe: "response" }).pipe(
      retryWithBackoff(options),
      map(response => this._mapSuccessfulResponse<T>(response))
    );
  }

  /**
   * Send a PUT request
   *
   * @param baseUri   The endpoint to send the data to
   * @param data      The data to send to the endpoint
   * @param options   Any request options
   */
  public put<T>(baseUri: string, data: any, options?: IHttpRequestOptions) {
    const uri = this._buildUri(baseUri, options);
    return this._http.put<T>(uri, data, { observe: "response" }).pipe(
      retryWithBackoff(options),
      map(response => this._mapSuccessfulResponse<T>(response))
    );
  }

  /**
   * Send a PATCH request
   *
   * @param baseUri   The endpoint to send the data to
   * @param data      The data to send to the endpoint
   * @param options   Any request options
   */
  public patch<T>(baseUri: string, data: any, options?: IHttpRequestOptions) {
    const uri = this._buildUri(baseUri, options);
    return this._http.patch<T>(uri, data, { observe: "response" }).pipe(
      retryWithBackoff(options),
      map(response => this._mapSuccessfulResponse<T>(response))
    );
  }

  /**
   * Send a DELETE request
   *
   * @param baseUri   The endpoint to send the data to
   * @param options   Any request options
   */
  public delete<T>(baseUri: string, options?: IHttpRequestOptions) {
    const uri = this._buildUri(baseUri, options);
    return this._http.delete<T>(uri, { observe: "response" }).pipe(
      retryWithBackoff(options),
      map(response => this._mapSuccessfulResponse<T>(response))
    );
  }

  /**
   * Build the URI from the base + options
   *
   * @param baseUri   The endpoint to send the data to
   * @param options   Any request options
   */
  protected _buildUri(baseUri: string, options?: IHttpRequestOptions): string {
    const URI = new URL(baseUri);
    if (options) {
      if (options.filter) {
        URI.searchParams.append("filter", options.filter);
      }
      if (options.orderBy) {
        URI.searchParams.append("orderBy", options.orderBy);
      }
      if (typeof options.pageSize === "number") {
        URI.searchParams.append("pageSize", options.pageSize.toString());
      }
      if (typeof options.pageNumber === "number") {
        URI.searchParams.append("pageNumber", options.pageNumber.toString());
      }
    }
    return URI.toString();
  }

  /**
   * Map a default HttpResponse to IHttpResponse
   */
  protected _mapSuccessfulResponse<T>(response: HttpResponse<T>): IHttpResponse<T> {
    const responseObject: IHttpResponse<T> = {
      status: response.status,
      message: null,
      data: response.body,
      pagination: response.headers && response.headers.has("X-Pagination") ? new Pagination(
        JSON.parse(response.headers.get("X-Pagination"))
      ) : null,
    };
    return responseObject;
  }
}
